import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
  SetShippingAddressesOnCartInput,
} from "../../../Common";
import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutation_Shipping_SetShippingAddressesOnCart_QueryKey } from "./data-mutation--shipping-set-shipping-addresses-on-cart--query-key-factory";

export interface DataMutationShippingSetShippingAddressesOnCartConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationShippingSetShippingAddressesOnCart_Result["data"]
  > {
  data: DataMutationShippingSetShippingAddressesOnCart_Result["data"];
  status: DataMutationShippingSetShippingAddressesOnCart_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationShippingSetShippingAddressesOnCartConsumerInterface;
const DataMutationShippingSetShippingAddressesOnCartContext =
  React.createContext(defaultValue);

export interface DataMutationShippingSetShippingAddressesOnCart_ControllerInterface {
  mutate: DataMutationShippingSetShippingAddressesOnCart_Result["mutate"];
  mutateAsync: DataMutationShippingSetShippingAddressesOnCart_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationShippingSetShippingAddressesOnCart_ControllerInterface;
const DataMutationShippingSetShippingAddressesOnCart_ControllerContext =
  React.createContext(controllerDefaultValue);

export interface DataMutationShippingSetShippingAddressesOnCart_Request
  extends SetShippingAddressesOnCartInput {}
export type DataMutationShippingSetShippingAddressesOnCart_Result =
  ReactQuery.UseMutationResult<
    CheckoutModel_MyBag,
    unknown,
    DataMutationShippingSetShippingAddressesOnCart_Request,
    unknown
  >;
export type GraphqlResponse_ShippingSetShippingAddressesOnCart =
  GraphqlWrappedData<
    "setShippingAddressesOnCart",
    {
      cart: CheckoutModel_MyBag;
    }
  >;

export interface DataMutationShippingSetShippingAddressesOnCartProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationShippingSetShippingAddressesOnCartProvider: React.FunctionComponent<
  DataMutationShippingSetShippingAddressesOnCartProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutation_Shipping_SetShippingAddressesOnCart_QueryKey.shippingSetShippingAddressesOnCart();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationShippingSetShippingAddressesOnCart_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          setShippingAddressesOnCart(
            input: {
              cart_id: "${request.cart_id}"
              shipping_addresses: [${request.shipping_addresses
                .map((shipping_address) => {
                  return `
                  {
                    address: {
                      firstname: "${shipping_address.address.firstname || ""}"
                      lastname: "${shipping_address.address.lastname || ""}"
                      company: null
                      street: [
                        ${shipping_address.address.street
                          .map(
                            (this_street) => `
                          "${this_street || ""}"
                        `
                          )
                          .join(", ")}
                      ]
                      city: "${shipping_address.address.city || ""}"
                      region: "${shipping_address.address.region || ""}"
                      region_id: ${shipping_address.address.region_id || 0}
                      postcode: "${shipping_address.address.postcode || ""}"
                      country_code: "${
                        shipping_address.address.country_code || ""
                      }"
                      telephone: "${shipping_address.address.telephone || ""}"
                      save_in_address_book: false
                    }
                  }
                `;
                })
                .join(", ")}]
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } =
        await axios.post<GraphqlResponse_ShippingSetShippingAddressesOnCart>(
          "/graphql",
          { query: gqlQuery },
          { headers }
        );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { setShippingAddressesOnCart } = gqlResponseData || {};
      const { cart } = setShippingAddressesOnCart || {};

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const ignoreMyCurrentState =
    genericDataPortController.ignoreMyCurrentStateFactory(mutation);
  const value: DataMutationShippingSetShippingAddressesOnCartConsumerInterface =
    React.useMemo(
      () => ({
        data: mutation.data,
        status: mutation.status,
        error: mutation.error,

        dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
        errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState: ignoreMyCurrentState,
      }),
      [
        mutation.data,
        mutation.status,
        mutation.error,

        genericDataPortValues.dataUpdatedAt,
        genericDataPortValues.errorUpdatedAt,
        ignoreMyCurrentState,
      ]
    );
  const controllerValue: DataMutationShippingSetShippingAddressesOnCart_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationShippingSetShippingAddressesOnCartContext.Provider
      value={value}
    >
      <DataMutationShippingSetShippingAddressesOnCart_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationShippingSetShippingAddressesOnCart_ControllerContext.Provider>
    </DataMutationShippingSetShippingAddressesOnCartContext.Provider>
  );
};

export const useDataMutationShippingSetShippingAddressesOnCart = () => {
  const context = React.useContext(
    DataMutationShippingSetShippingAddressesOnCartContext
  );
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationShippingSetShippingAddressesOnCart must be used within a DataMutationShippingSetShippingAddressesOnCartProvider"
    );
  }
  return context;
};

export const useDataMutationShippingSetShippingAddressesOnCart_asController =
  () => {
    const context = React.useContext(
      DataMutationShippingSetShippingAddressesOnCart_ControllerContext
    );
    if (context === controllerDefaultValue) {
      throw new Error(
        "useDataMutationShippingSetShippingAddressesOnCart_asController must be used within a DataMutationShippingSetShippingAddressesOnCartProvider"
      );
    }
    return context;
  };
